/**
 * Client-side image compression for the web app.
 * Mirrors the mobile app's compressPhoto logic using Canvas API.
 *
 * - Resizes to max 1920px on the longest side
 * - Compresses as JPEG, targeting under 500 KB
 * - Progressively lowers quality if still too large
 */

import * as Sentry from "@sentry/react";

const MAX_DIMENSION = 1920;
const MAX_BYTES = 500 * 1024; // 500 KB
const QUALITY_STEPS = [0.8, 0.6, 0.5, 0.4, 0.3];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/jpeg",
      quality,
    );
  });
}

/**
 * Compress an image File. Returns a new File (JPEG) under MAX_BYTES if possible.
 * Non-image files are returned as-is.
 */
export async function compressImage(file: File): Promise<File> {
  // Only compress images
  if (!file.type.startsWith("image/")) return file;

  // Small enough already — skip
  if (file.size <= MAX_BYTES) return file;

  const img = await loadImage(file);

  // Compute scaled dimensions
  let { width, height } = img;
  if (Math.max(width, height) > MAX_DIMENSION) {
    if (width >= height) {
      height = Math.round((height * MAX_DIMENSION) / width);
      width = MAX_DIMENSION;
    } else {
      width = Math.round((width * MAX_DIMENSION) / height);
      height = MAX_DIMENSION;
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);

  // Release object URL
  URL.revokeObjectURL(img.src);

  // Try each quality level until under target
  for (const quality of QUALITY_STEPS) {
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= MAX_BYTES) {
      const name = file.name.replace(/\.\w+$/, ".jpg");
      return new File([blob], name, { type: "image/jpeg" });
    }
  }

  // Last resort: lowest quality result even if over target
  const blob = await canvasToBlob(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]);
  const name = file.name.replace(/\.\w+$/, ".jpg");
  return new File([blob], name, { type: "image/jpeg" });
}

/**
 * How many images to decode/compress at the same time. Each in-flight image
 * holds a full-resolution bitmap + canvas in memory, so compressing a large
 * selection all at once (Promise.all over everything) spikes RAM enough to
 * crash the browser tab. Process in small chunks so peak memory stays bounded.
 */
const COMPRESS_CONCURRENCY = 3;

/**
 * Compress an array of Files with bounded concurrency (memory-safe for large
 * batches). Order is preserved.
 */
export async function compressImages(files: File[]): Promise<File[]> {
  Sentry.addBreadcrumb({
    category: "photos",
    message: "compressImages: batch",
    level: "info",
    data: {
      count: files.length,
      totalBytes: files.reduce((sum, f) => sum + (f.size || 0), 0),
    },
  });
  const results: File[] = new Array(files.length);
  for (let i = 0; i < files.length; i += COMPRESS_CONCURRENCY) {
    const chunk = files.slice(i, i + COMPRESS_CONCURRENCY);
    const compressed = await Promise.all(chunk.map(compressImage));
    for (let j = 0; j < compressed.length; j++) {
      results[i + j] = compressed[j];
    }
  }
  return results;
}
